import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/navbar';
import Footer from '../components/footer';

const DiscountProducts = () => {
    const [cartCount, setCartCount] = useState(0);
    const [forceUpdateKey, setForceUpdateKey] = useState(0);
    const [products, setProducts] = useState([]);
    const [cartItems, setCartItems] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const fetchDiscountProducts = async () => {
            try {
                const response = await fetch(
                    // 'http://localhost:3005/discount'
                    'https://afric-fresh-backend.onrender.com/discount'
                );
                if (response.ok) {
                    const data = await response.json();
                    setProducts(data);
                } else {
                    console.error('Failed to fetch products');
                }
            } catch (error) {
                console.error('Error fetching products:', error);
            }
        };

        fetchDiscountProducts();
        const storedCart = JSON.parse(localStorage.getItem('cartItems')) || [];
        setCartItems(storedCart);
        setCartCount(storedCart.reduce((acc, item) => acc + item.quantity, 0));
    }, []);

    const updateCartCount = () => {
        const storedCartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
        setCartCount(storedCartItems.reduce((acc, item) => acc + item.quantity, 0));
    };

    const forceUpdateProducts = () => {
        setForceUpdateKey(prevKey => prevKey + 1);
    };

    const ProductCard = ({ product, originalPrice, updateCartCount, forceUpdateKey }) => {
        const [inCart, setInCart] = useState(false);
        const [quantity, setQuantity] = useState(0);
        const navigate = useNavigate();

        useEffect(() => {
            const storedCartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
            const foundItem = storedCartItems.find((item) => item.id === product.id);
            if (foundItem) {
                setInCart(true);
                setQuantity(foundItem.quantity);
            } else {
                setInCart(false);
                setQuantity(0);
            }
        }, [product.id, forceUpdateKey]);

        const handleAddToCart = () => {
            const storedCartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
            const hasNonDiscountItems = storedCartItems.some(item => item.orderType !== 'Discount');

            if (hasNonDiscountItems) {
                setErrorMessage('Please complete or clear your current cart before adding discounted products.');
                return;
            }

            setInCart(true);
            setQuantity(1);
            saveToLocalStorage(product, 1);
            updateCartCount();
        };

        const handleQuantityChange = (newQuantity) => {
            setQuantity(newQuantity);
            const cartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
            const updatedCart = cartItems.map(item =>
                item.id === product.id ? { ...item, quantity: newQuantity } : item
            );
            localStorage.setItem('cartItems', JSON.stringify(updatedCart));
            updateCartCount();
        };

        const saveToLocalStorage = (product, quantity) => {
            const cartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
            const updatedCart = [
                ...cartItems,
                { ...product, quantity, orderType: 'Discount' }
            ];
            localStorage.setItem('cartItems', JSON.stringify(updatedCart));
        };

        return (
            <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                <div className="relative">
                    <div className="absolute top-2 right-2 bg-orange-500 text-white px-2 py-1 rounded-md">
                        -{product.discount}%
                    </div>
                    <img
                        src={product.img}
                        alt={product.name}
                        className="w-full h-64 object-cover"
                    />
                </div>
                <div className="p-4">
                    <h3 className="text-lg font-semibold mb-2">{product.name}</h3>
                    <p className="text-gray-600 mb-1">{product.brand}</p>
                    <div className="flex items-center gap-2 mb-3">
                        <span className="text-gray-400 line-through text-sm">
                            ${parseFloat(originalPrice).toLocaleString()}
                        </span>
                        <span className="text-xl font-bold text-green-800">
                            ${product.price ? product.price.toLocaleString() : 'N/A'}
                        </span>
                    </div>
                    <p className="text-sm text-gray-700 mt-1">{product.description}</p>
                    <p className="text-gray-600 mb-3">{product.quantity} items left</p>

                    {inCart ? (
                        <div className="flex items-center justify-between bg-gray-100 rounded-full p-1">
                            <button
                                onClick={() => quantity > 1 && handleQuantityChange(quantity - 1)}
                                className="bg-green-600 text-white h-8 w-8 rounded-full"
                            >
                                -
                            </button>
                            <span className="font-semibold">{quantity}</span>
                            <button
                                onClick={() => handleQuantityChange(quantity + 1)}
                                className="bg-green-700 text-white h-8 w-8 rounded-full"
                            >
                                +
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={handleAddToCart}
                            className="w-full bg-green-700 text-white py-2 rounded-lg hover:bg-green-800"
                        >
                            Add to cart
                        </button>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen flex flex-col">
            <Navbar 
                cartCount={cartCount} 
                updateCartCount={updateCartCount} 
                forceUpdateProducts={forceUpdateProducts} 
            />
            <div className="flex-grow container mt-28 px-4 mb-20">
                <h2 className="text-2xl font-bold mb-6">Discounted Products</h2>
                {errorMessage && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
                        <span className="block sm:inline">{errorMessage}</span>
                    </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mx-auto px-4">
                    {products.map(product => {
                        const calculatedOriginalPrice = product.price 
                            ? (product.price / (1 - product.discount / 100)).toFixed(2)
                            : null;

                        return (
                            <ProductCard
                                key={product.id}
                                product={product}
                                originalPrice={calculatedOriginalPrice}
                                updateCartCount={updateCartCount}
                                forceUpdateKey={forceUpdateKey}
                            />
                        );
                    })}
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default DiscountProducts;